/**
 * lib/store/seed.ts (Phase 4)
 *
 * Imports the QR JSON seeds and exposes loadSeed() returning fully-typed
 * initial state for every store slice — including Phase 4 additions
 * (invoices, feedbacks, transports) and Phase 2/3 push notifications.
 */
import stationsJson from '@/lib/mock-data/qr/stations.json';
import hotelsJson from '@/lib/mock-data/qr/hotels.json';
import crewJson from '@/lib/mock-data/qr/crew.json';
import flightsJson from '@/lib/mock-data/qr/flights.json';
import trainingsJson from '@/lib/mock-data/qr/trainings.json';
import officeDutiesJson from '@/lib/mock-data/qr/office-duties.json';
import surfaceTravelJson from '@/lib/mock-data/qr/surface-travel.json';
import rosterJson from '@/lib/mock-data/qr/roster-may2026.json';
import fxRatesJson from '@/lib/mock-data/qr/fx-rates.json';
import seedBookingsJson from '@/lib/mock-data/qr/seed-bookings.json';
import chatSeedJson from '@/lib/mock-data/qr/chat-seed.json';
import broadcastSeedJson from '@/lib/mock-data/qr/broadcast-seed.json';
import inventoryAlertsJson from '@/lib/mock-data/qr/inventory-alerts-seed.json';
// Phase 2/3
import pushSeedJson from '@/lib/mock-data/qr/push-seed.json';
import transportsSeedJson from '@/lib/mock-data/qr/transports-seed.json';
// Phase 4
import invoicesSeedJson from '@/lib/mock-data/qr/invoices-seed.json';
import feedbackSeedJson from '@/lib/mock-data/qr/feedback-seed.json';
// Prompt 3
import outboxSeedJson from '@/lib/mock-data/qr/outbox-seed.json';

import type {
  AccrualEntry,
  Booking,
  Broadcast,
  BroadcastResponse,
  ChatMessage,
  ChatThread,
  Crew,
  CrewFeedback,
  Flight,
  FxRate,
  Hotel,
  InventoryAlert,
  Invoice,
  NotificationOutboxEntry,
  OfficeDuty,
  PushNotification,
  RosterLine,
  RosterPublication,
  Station,
  SurfaceTravel,
  Training,
  Transport,
} from '@/lib/types';

export interface SeedBundle {
  stations: Station[];
  hotels: Hotel[];
  crew: Crew[];
  flights: Flight[];
  trainings: Training[];
  officeDuties: OfficeDuty[];
  surfaceTravel: SurfaceTravel[];
  publications: RosterPublication[];
  rosterLines: RosterLine[];
  fxRates: FxRate[];
  bookings: Booking[];
  chatThreads: ChatThread[];
  chatMessages: ChatMessage[];
  broadcasts: Broadcast[];
  broadcastResponses: BroadcastResponse[];
  inventoryAlerts: InventoryAlert[];
  pushNotifications: PushNotification[];
  transports: Transport[];
  invoices: Invoice[];
  feedbacks: CrewFeedback[];
  outbox: NotificationOutboxEntry[];
  accruals: AccrualEntry[];
}

interface RosterFile { publication: RosterPublication; lines: RosterLine[]; }
interface ChatFile { threads: ChatThread[]; messages: ChatMessage[]; }
interface BroadcastFile { broadcasts: Broadcast[]; responses: BroadcastResponse[]; }

export function loadSeed(): SeedBundle {
  const roster = rosterJson as unknown as RosterFile;
  const chat = chatSeedJson as unknown as ChatFile;
  const bcast = broadcastSeedJson as unknown as BroadcastFile;

  // Generate accrual entries from bookings with proper stage classification
  // Add default OTP fields to bookings loaded from JSON
  const bookings = (seedBookingsJson as unknown as Partial<Booking>[]).map((b) => ({
    ...b,
    checkin_otp_sent: b.checkin_otp_sent ?? false,
    checkin_otp_regen_count: b.checkin_otp_regen_count ?? 0,
    checkin_confirmed_at: b.checkin_confirmed_at ?? null,
    checkin_confirmed_by_id: b.checkin_confirmed_by_id ?? null,
    checkout_otp_sent: b.checkout_otp_sent ?? false,
    checkout_otp_regen_count: b.checkout_otp_regen_count ?? 0,
    checkout_confirmed_at: b.checkout_confirmed_at ?? null,
    checkout_confirmed_by_id: b.checkout_confirmed_by_id ?? null,
  })) as Booking[];
  const invoices = invoicesSeedJson as Invoice[];
  const accruals: AccrualEntry[] = [];
  const currentDate = '2026-05-09T08:00:00Z'; // Match current date from env

  // Build invoice lookup map: booking_id → { invoice, line }
  const invoiceMap = new Map<string, { invoice: Invoice; line: { id: string; total: number } }>();
  for (const invoice of invoices) {
    for (const line of invoice.lines) {
      if (line.booking_id) {
        invoiceMap.set(line.booking_id, {
          invoice,
          line: { id: line.id, total: line.total },
        });
      }
    }
  }

  // Process all bookings
  for (const booking of bookings) {
    const checkinDate = booking.check_in_utc.slice(0, 10);
    const checkoutDate = booking.check_out_utc.slice(0, 10);
    const checkinTime = new Date(checkinDate).getTime();
    const checkoutTime = new Date(checkoutDate).getTime();
    const currentTime = new Date(currentDate).getTime();

    // Skip if booking hasn't started yet (check-in is in the future)
    if (checkinTime > currentTime) {
      continue;
    }

    const invoiceData = invoiceMap.get(booking.id);

    let stage: AccrualEntry['stage'];
    let invoicedAmount: number | null = null;
    let paidAmount: number | null = null;
    let invoiceId: string | null = null;
    let invoiceLineId: string | null = null;
    let invoicedAt: string | null = null;
    let paidAt: string | null = null;
    let agingDays: number;

    // Determine stage based on checkout status and invoice status
    if (checkoutTime >= currentTime && booking.status === 'CHECKED_IN') {
      // Crew is currently staying (checked in, checkout hasn't happened yet)
      // → CONSUMED (uninvoiced, service being consumed)
      stage = 'CONSUMED';
      agingDays = 0; // No aging yet, still consuming service
    } else if (checkoutTime < currentTime) {
      // Checkout has passed
      agingDays = Math.floor((currentTime - checkoutTime) / (1000 * 60 * 60 * 24));

      if (!invoiceData) {
        // checkout done + no invoice → ACCRUED
        stage = 'ACCRUED';
      } else if (invoiceData.invoice.approval_status === 'FINANCE_APPROVED') {
        // invoice exists + paid → PAID
        stage = 'PAID';
        invoicedAmount = invoiceData.line.total;
        paidAmount = invoiceData.line.total;
        invoiceId = invoiceData.invoice.id;
        invoiceLineId = invoiceData.line.id;
        invoicedAt = invoiceData.invoice.uploaded_at;
        paidAt = invoiceData.invoice.uploaded_at; // Use uploaded_at as proxy for payment
      } else {
        // invoice exists + not paid → INVOICED
        stage = 'INVOICED';
        invoicedAmount = invoiceData.line.total;
        invoiceId = invoiceData.invoice.id;
        invoiceLineId = invoiceData.line.id;
        invoicedAt = invoiceData.invoice.uploaded_at;
      }
    } else {
      // Skip bookings that haven't checked in yet
      continue;
    }

    accruals.push({
      id: `acc_${booking.id}`,
      booking_id: booking.id,
      hotel_id: booking.hotel_id,
      station_iata: booking.station_iata,
      checkout_date: checkoutDate,
      expected_amount: booking.total_tcoat,
      invoiced_amount: invoicedAmount,
      paid_amount: paidAmount,
      currency: booking.currency,
      stage,
      invoice_id: invoiceId,
      invoice_line_id: invoiceLineId,
      invoiced_at: invoicedAt,
      paid_at: paidAt,
      aging_days: agingDays,
      accounting_period: checkoutDate.slice(0, 7),
      journal_ref: '',
      finance_notes: '',
      invoice_ref: '',
      created_at: currentDate,
      updated_at: currentDate,
    });
  }

  return {
    stations: stationsJson as Station[],
    hotels: hotelsJson as Hotel[],
    crew: crewJson as Crew[],
    flights: flightsJson as Flight[],
    trainings: trainingsJson as Training[],
    officeDuties: officeDutiesJson as OfficeDuty[],
    surfaceTravel: surfaceTravelJson as SurfaceTravel[],
    publications: [roster.publication],
    rosterLines: roster.lines,
    fxRates: fxRatesJson as FxRate[],
    bookings,
    chatThreads: chat.threads,
    chatMessages: chat.messages,
    broadcasts: bcast.broadcasts,
    broadcastResponses: bcast.responses,
    inventoryAlerts: inventoryAlertsJson as InventoryAlert[],
    pushNotifications: pushSeedJson as PushNotification[],
    transports: transportsSeedJson as Transport[],
    invoices: invoicesSeedJson as Invoice[],
    feedbacks: feedbackSeedJson as CrewFeedback[],
    outbox: outboxSeedJson as NotificationOutboxEntry[],
    accruals,
  };
}
